import type { ReactNode } from "react";

import { cn } from "../../lib/utils";
import { PluginIcon } from "./pluginIcons";
import type { ForgePluginId } from "./pluginsPage.logic";

/**
 * Row chrome shared by every plugin list, in the settings design standard's
 * item-row language (`settings/settingsLayout.tsx`, ported from t3code).
 *
 * The two properties that make a list read as one system are both here rather
 * than at each call site: peers are separated by **whitespace and a rounded
 * hover plane**, never by a rule, and the three columns are declared once so
 * names and controls line up down the page instead of tracking each row's own
 * content width. Rows that want the same alignment inside a single link target
 * take `grid-cols-subgrid` off this parent — see `PluginRow`.
 *
 * Lives in its own module so `PluginRow` and the section components can both
 * reach it without importing each other.
 */
export const PLUGIN_ROW_CLASS =
  "grid min-h-touch grid-cols-[auto_minmax(0,1fr)] items-center gap-3 rounded-xl px-3 py-3 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:gap-x-4 sm:px-4";

/**
 * The control cell. Below `sm` it drops to its own line under the text rather
 * than competing for width with it — the same stacking the ported
 * `SettingsRow` does, and for the same reason: holding three columns at 320px
 * left the name and description as ellipses ("Google ...", "Search ...").
 */
export const PLUGIN_ROW_CONTROL_CLASS =
  "col-start-2 justify-self-start sm:col-start-3 sm:justify-self-end";

/** The navigable icon + text span of a row, aligned to the row's own columns. */
export const PLUGIN_ROW_LINK_CLASS =
  "col-span-2 grid grid-cols-subgrid items-center gap-3 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:gap-4";

interface PluginIconTileProps {
  readonly pluginId: ForgePluginId;
  readonly className?: string;
}

/**
 * The brand tile. `bg-card` is one rung above the page canvas on the surface
 * ladder, which is what separates it — not a shadow. `border-border` is used
 * bare: it is already the subtle value, and an opacity modifier on it compounds
 * with the alpha the token already carries.
 */
export function PluginIconTile({ pluginId, className }: PluginIconTileProps): ReactNode {
  return (
    <span
      data-slot="plugin-icon-tile"
      className={cn(
        "flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-card",
        className,
      )}
    >
      <PluginIcon pluginId={pluginId} className="size-6" />
    </span>
  );
}

interface PluginRowTextProps {
  readonly name: ReactNode;
  readonly description: string;
  readonly badge?: ReactNode;
}

/**
 * Name over description.
 *
 * The description steps down to the settings standard's 13px only from `sm`.
 * Below that it stays `text-sm`, because 13px is under the readable floor for
 * body copy on a touch viewport and this line is how the user tells two
 * plugins apart.
 */
export function PluginRowText({ name, description, badge }: PluginRowTextProps): ReactNode {
  return (
    <span className="min-w-0">
      <span className="flex min-w-0 flex-wrap items-center gap-2">
        <span className="truncate text-sm font-medium tracking-[-0.005em] text-foreground">
          {name}
        </span>
        {badge}
      </span>
      {/* Wraps to a second line on a narrow viewport instead of truncating:
          this line is how the user tells two plugins apart, and there is room
          for it once the control has stacked below. */}
      <span className="mt-0.5 block line-clamp-2 text-sm leading-[1.45] text-muted-foreground sm:truncate sm:text-[13px]">
        {description}
      </span>
    </span>
  );
}
